import { FormEvent, useRef, useState } from 'react';
import { consultants } from '../data/consultants';
import { buildMailto, submitInquiry, type InquiryType } from '../utils/submitInquiry';

export type FormStatus = 'idle' | 'submitting' | 'success' | 'error' | 'not_configured';
type FieldValues = Record<string, string | boolean>;

interface UseInquiryFormOptions<T extends FieldValues> {
  formId: string;
  type: InquiryType;
  initialValues: T;
  validate: (values: T) => Partial<Record<string, string>>;
  mailSubject: string;
  summarize: (values: T) => string[];
  file?: File | null;
}

const MIN_FILL_MS = 2500;

export function useInquiryForm<T extends FieldValues>({
  formId,
  type,
  initialValues,
  validate,
  mailSubject,
  summarize,
  file = null,
}: UseInquiryFormOptions<T>) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const startedAt = useRef(Date.now());

  function clearError(key: string) {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (status === 'error' || status === 'not_configured') setStatus('idle');
  }

  function setField<K extends keyof T>(key: K, value: T[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
    clearError(String(key));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'submitting') return;

    const found = validate(values);
    const invalidKeys = Object.keys(found).filter((key) => found[key]);
    setErrors(found);
    if (invalidKeys.length) {
      setStatus('idle');
      requestAnimationFrame(() => document.getElementById(`${formId}-${invalidKeys[0]}`)?.focus());
      return;
    }

    if (honeypot || Date.now() - startedAt.current < MIN_FILL_MS) {
      setStatus('error');
      setMessage('We could not verify this submission. Please wait a moment and try again.');
      return;
    }

    setStatus('submitting');
    setMessage('');
    const result = await submitInquiry(type, values, file);
    if (result.ok) {
      setStatus('success');
      return;
    }
    setStatus(result.reason === 'not_configured' ? 'not_configured' : 'error');
    setMessage(result.message);
  }

  function reset() {
    setValues(initialValues);
    setErrors({});
    setStatus('idle');
    setMessage('');
    startedAt.current = Date.now();
  }

  const mailtoHref = buildMailto(
    consultants.map((c) => c.email),
    mailSubject,
    summarize(values),
  );

  return { values, errors, status, message, setField, clearError, setErrors, handleSubmit, honeypot, setHoneypot, mailtoHref, reset };
}
